import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, Group, Box, Text, Flex, Button } from "@mantine/core";
import { useParams, Link } from "react-router-dom";
import { IconThumbUp, IconThumbDown } from "@tabler/icons-react";
import toast from "react-hot-toast";

import { ErrorView, PageTitle } from "@components/shared";
import { SurveyForm } from "@components/Survey";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

import { surveyRequest } from "@dummy/survey-request";
import { Summary } from "@legacy/SurveyRequest";

export default function RequestDetail() {
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const params = useParams();
  const { id } = params;

  useEffect(() => {
    fetchData();
  }, []);

  function fetchData() {
    axios
      .get(`/requests/${id}/`)
      .then(({ data }) => {
        setRequest(data.response[0]);
      })
      .catch((err) => {
        // setError(err);
        setRequest(surveyRequest);
      });
  }

  function onDecisionClick(decision) {
    const req = {
      approve: decision,
      survey_invite_id: id,
    };

    setLoading(true);

    axios
      .post(`/survey-invites/${id}/approval/`, req)
      .then(() => {
        toast.success(decision ? "Approved!" : "Denied!");
        fetchData();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!request) return null;

  const showDecisionButtons = request.approval_status === 0;
  const is360 = request.is_360;

  return (
    <div>
      <PageTitle
        title={`${request.requested_for_formatted.name}`}
        subtitle={`${request.requested_by.full_name} requested on ${new Date(
          request.created_at
        ).toLocaleDateString()}`}
        icon={entityIcon.request()}
        iconColor={entityColor.request}
      />
      {showDecisionButtons ? (
        <Card mt="lg">
          <Flex gap="xs">
            <Button color="green" onClick={() => onDecisionClick(true)}>
              Approve
            </Button>
            <Button color="red" onClick={() => onDecisionClick(false)}>
              Deny
            </Button>
          </Flex>
        </Card>
      ) : (
        <Card mt="lg">
          <Flex gap="xs" align="center">
            <div>
              {request.approval_status === 1 ? (
                <IconThumbDown size={20} color="var(--mantine-color-red-6)" />
              ) : (
                <IconThumbUp size={20} color="var(--mantine-color-green-6)" />
              )}
            </div>
            <Text fw={700} c={request.approval_status === 1 ? "red" : "green"}>
              This survey request was{" "}
              {request.approval_status === 1 ? "denied" : "approved"}. No
              further action is required.
            </Text>
          </Flex>
        </Card>
      )}
      {is360 && (
        <Card mt="lg">
          <Flex gap="xs" direction={{ base: "column", sm: "row" }}>
            <Button
              size="xs"
              onClick={() => {
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/360/${request.id}`
                  );
                  toast.success("Copied!");
                }
              }}
            >
              Copy public link
            </Button>
            <Button component={Link} to={`/report-360/${request.id}`} size="xs">
              View 360
            </Button>
            <Close360Button
              closed={request.closed}
              surveyContainerId={request.survey_container_id}
              onSuccess={fetchData}
            />
            <EmailButton
              released={request.released}
              surveyContainerId={request.survey_container_id}
              onSuccess={fetchData}
            />
            {["qa", "dev"].includes(import.meta.env.VITE_ENV) && (
              <AutoRespond
                surveyContainerId={request.survey_container_id}
                onSuccess={fetchData}
              />
            )}
          </Flex>
        </Card>
      )}
      <Card mt="lg">
        {!request.engagement_survey_name && (
          <Box>
            <Summary
              companyName={request.requested_for_formatted.name}
              employeeName={
                request.employee ? request.employee.full_name : "Unknown"
              }
              requesterName={request.requested_by.full_name}
              surveys={request.surveys_info}
              showResend={request.approval_status === 2}
            />
          </Box>
        )}
        {request.engagement_survey_name && (
          <Box>
            <Summary
              companyName={request.requested_for_formatted.name}
              surveyName={request.engagement_survey_name}
              companyId={request.requested_for_formatted.id}
            />
          </Box>
        )}
      </Card>
    </div>
  );
}

function Close360Button({ closed, surveyContainerId, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);

    axios
      .get(`/survey-containers/${surveyContainerId}/toggle-close/`)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  };

  return (
    <Button onClick={onClick} loading={loading} size="xs">
      {closed ? `Re-open 360` : "Close 360"}
    </Button>
  );
}

function EmailButton({ released, surveyContainerId, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);

    axios
      .post(`/survey-containers/${surveyContainerId}/release-survey/`, {
        survey_container_id: surveyContainerId,
      })
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  };

  return (
    <Button onClick={onClick} loading={loading} size="xs">
      {released ? "Rescind release" : "Release results"}
    </Button>
  );
}

function AutoRespond({ surveyContainerId, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);

    axios
      .get(`/survey-containers/${surveyContainerId}/auto-respond/`)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  };

  return (
    <Button onClick={onClick} loading={loading} size="xs">
      Auto-respond
    </Button>
  );
}
